import React, { useState } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertCircle, CheckCircle, Clock, RefreshCw, Power,
  Cpu, HardDrive, Zap, AlertTriangle, Loader2, GitBranch
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './WorkerStatusPanel.css';

function WorkerStatusPanel() {
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['scraper', 'worker', 'status'],
    queryFn: () => apiClient.get('/api/scraper/worker/status'),
    refetchInterval: 10000, // Poll every 10 seconds
  });

  const sendCommand = useMutation({
    mutationFn: async ({ command, workerId }) => {
      setActionLoading(command);
      return apiClient.post('/api/scraper/worker/command', {
        command,
        worker_id: workerId || null
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper', 'worker', 'status'] });
    },
    onSettled: () => {
      setActionLoading(null);
    }
  });

  const handleHealthCheck = (workerId) => {
    sendCommand.mutate({ command: 'health-check', workerId });
  };

  const handleRestart = async (workerId) => {
    if (await confirmDialog('Are you sure you want to restart the worker? Current jobs will complete first.')) {
      sendCommand.mutate({ command: 'restart', workerId });
    }
  };

  const handleGitPullRestart = async (workerId) => {
    if (await confirmDialog('This will pull the latest code from GitHub and restart the worker. Continue?')) {
      sendCommand.mutate({ command: 'git-pull-restart', workerId });
    }
  };

  if (isLoading) {
    return (
      <div className="wsp-container wsp-loading">
        <Loader2 className="wsp-spin" size={20} />
        <span>Loading worker status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wsp-container wsp-error">
        <AlertCircle size={20} />
        <span>Failed to load worker status: {error.message}</span>
      </div>
    );
  }

  const workers = data?.workers || [];
  const hasOnlineWorker = data?.hasOnlineWorker;

  if (workers.length === 0) {
    return (
      <div className="wsp-container wsp-no-workers">
        <AlertTriangle size={20} />
        <div>
          <strong>No workers registered</strong>
          <p>Start the scraper worker to begin processing jobs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wsp-container">
      {/* Overall Status Banner */}
      {!hasOnlineWorker && (
        <div className="wsp-alert wsp-alert-offline">
          <AlertCircle size={16} />
          <span>All workers are offline - jobs will not process until a worker comes online</span>
        </div>
      )}

      {/* Workers List */}
      <div className="wsp-workers">
        {workers.map((worker) => {
          const isOnline = worker.effective_status !== 'offline';
          const isProcessing = worker.effective_status === 'processing';
          const isError = worker.effective_status === 'error';
          const secondsSinceHeartbeat = Math.round(worker.seconds_since_heartbeat || 0);

          return (
            <div
              key={worker.worker_id}
              className={`wsp-worker-card ${isOnline ? '' : 'wsp-offline'} ${isProcessing ? 'wsp-processing' : ''} ${isError ? 'wsp-error-state' : ''}`}
            >
              <div className="wsp-worker-header">
                <div className="wsp-worker-identity">
                  <span className={`wsp-status-dot ${isOnline ? (isProcessing ? 'processing' : isError ? 'error' : 'online') : 'offline'}`} />
                  <span className="wsp-worker-id">{worker.worker_id}</span>
                  {worker.hostname && worker.hostname !== worker.worker_id && (
                    <span className="wsp-hostname">({worker.hostname})</span>
                  )}
                </div>
                <div className="wsp-worker-status-badge">
                  {isProcessing ? (
                    <span className="wsp-badge wsp-badge-processing">
                      <Loader2 size={12} className="wsp-spin" />
                      Processing
                    </span>
                  ) : isError ? (
                    <span className="wsp-badge wsp-badge-error">
                      <AlertCircle size={12} />
                      Error
                    </span>
                  ) : isOnline ? (
                    <span className="wsp-badge wsp-badge-online">
                      <CheckCircle size={12} />
                      Online
                    </span>
                  ) : (
                    <span className="wsp-badge wsp-badge-offline">
                      <AlertCircle size={12} />
                      Offline
                    </span>
                  )}
                </div>
              </div>

              <div className="wsp-worker-metrics">
                <div className="wsp-metric">
                  <Clock size={14} />
                  <span>
                    {isOnline
                      ? `Last seen ${secondsSinceHeartbeat}s ago`
                      : `Offline for ${formatDuration(secondsSinceHeartbeat)}`}
                  </span>
                </div>

                {worker.uptime_seconds && (
                  <div className="wsp-metric">
                    <Activity size={14} />
                    <span>Uptime: {formatDuration(worker.uptime_seconds)}</span>
                  </div>
                )}

                {worker.memory_mb && (
                  <div className="wsp-metric">
                    <HardDrive size={14} />
                    <span>Memory: {worker.memory_mb} MB</span>
                  </div>
                )}

                <div className="wsp-metric">
                  <Zap size={14} />
                  <span>
                    Jobs: {worker.jobs_completed || 0} completed, {worker.jobs_failed || 0} failed
                  </span>
                </div>

                {isProcessing && worker.current_job_type && (
                  <div className="wsp-metric wsp-current-job">
                    <Cpu size={14} />
                    <span>
                      Running: {worker.current_job_type}
                      {worker.current_job_id && ` (${worker.current_job_id.substring(0, 8)}...)`}
                    </span>
                  </div>
                )}

                {isError && worker.error_message && (
                  <div className="wsp-metric wsp-error-msg">
                    <AlertTriangle size={14} />
                    <span>{worker.error_message}</span>
                  </div>
                )}
              </div>

              <div className="wsp-worker-actions">
                <button
                  className="wsp-btn wsp-btn-secondary"
                  onClick={() => handleHealthCheck(worker.worker_id)}
                  disabled={!isOnline || actionLoading}
                  title="Send health check command"
                >
                  {actionLoading === 'health-check' ? (
                    <Loader2 size={14} className="wsp-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Check
                </button>

                <button
                  className="wsp-btn wsp-btn-primary"
                  onClick={() => handleGitPullRestart(worker.worker_id)}
                  disabled={!isOnline || actionLoading}
                  title="Pull latest code from GitHub and restart"
                >
                  {actionLoading === 'git-pull-restart' ? (
                    <Loader2 size={14} className="wsp-spin" />
                  ) : (
                    <GitBranch size={14} />
                  )}
                  Update & Restart
                </button>

                <button
                  className="wsp-btn wsp-btn-danger"
                  onClick={() => handleRestart(worker.worker_id)}
                  disabled={!isOnline || actionLoading}
                  title="Restart worker (current job will finish first)"
                >
                  {actionLoading === 'restart' ? (
                    <Loader2 size={14} className="wsp-spin" />
                  ) : (
                    <Power size={14} />
                  )}
                  Restart
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Commands */}
      {data?.recentCommands?.length > 0 && (
        <div className="wsp-commands">
          <h4>Recent Commands</h4>
          <div className="wsp-commands-list">
            {data.recentCommands.slice(0, 5).map((cmd) => (
              <div key={cmd.id} className={`wsp-command wsp-command-${cmd.status}`}>
                <span className="wsp-command-type">{cmd.command}</span>
                <span className="wsp-command-status">{cmd.status}</span>
                <span className="wsp-command-time">
                  {new Date(cmd.created_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

export default WorkerStatusPanel;
